import { z } from "zod"

import {
  portalItemListLimits,
  portalItemListMax,
  portalTableCellMaxLength,
  portalTableMaxRows,
} from "../../portal-item-answers.js"
import { portalShowWhenProblem } from "../../portal-item-visibility.js"
import { portalRequestItemKinds } from "../../portal-requests.js"

/* The parts of a request item that a portal request and an engagement
   request share. Both item schemas build on these, so a new kind or answer
   shape is added once. */

export const PortalRequestItemKindSchema = z.enum(portalRequestItemKinds)

/** The choices, the table's columns or the to-dos. Each kind's own count
    is checked with portalItemListProblem. */
export const PortalItemOptionsInputSchema = z
  .array(z.string().trim().min(1).max(120))
  .max(portalItemListMax)
  .default([])

const tableRow = (cell: z.ZodString) =>
  z.array(cell).max(portalItemListLimits.table.max)

const tableRows = (cell: z.ZodString) =>
  z.array(tableRow(cell)).max(portalTableMaxRows)

const pickList = (entry: z.ZodString) => z.array(entry).max(portalItemListMax)

/* What the API returns is read-only, as the stored answer is, so a stored
   answer goes out without a copy. */
const storedTableRows = z
  .array(tableRow(z.string().max(portalTableCellMaxLength)).readonly())
  .max(portalTableMaxRows)
  .readonly()

/** An answer as the API returns it. */
export const PortalAnswerValueSchema = z.union([
  z.string().max(2000),
  z.boolean(),
  pickList(z.string().max(120)).readonly(),
  storedTableRows,
])

/** An answer as the portal or the firm sends it. Null clears it. The shape
    each kind takes is checked with isPortalRequestAnswerValueValid. */
export const PortalAnswerBodyValueSchema = z.union([
  z.string().trim().max(2000),
  z.boolean(),
  pickList(z.string().trim().max(120)),
  tableRows(z.string().trim().max(portalTableCellMaxLength)),
  z.null(),
])

/** What a draft holds: the text being typed, or a table's rows. */
export const PortalDraftValueSchema = z.union([
  z.string().max(2000),
  storedTableRows,
])

const ShowWhenAnswerSchema = z.union([
  z.string().trim().min(1).max(120),
  z.boolean(),
])

/** A stored condition, as the API returns it. */
export const PortalItemShowWhenSchema = z.object({
  itemId: z.string().uuid(),
  equals: ShowWhenAnswerSchema,
})

/** A condition as a builder sends it, naming an earlier item in the same
    list. The list as a whole is checked with portalShowWhenProblem. */
export const PortalItemShowWhenInputSchema = z.object({
  itemName: z.string().trim().min(1).max(120),
  equals: ShowWhenAnswerSchema,
})

/** Adds the first condition in a list that cannot work to the list's
    issues, on the item that holds it. */
export const checkPortalShowWhen = (
  items: Parameters<typeof portalShowWhenProblem>[0],
  context: z.RefinementCtx
) => {
  const problem = portalShowWhenProblem(items)
  if (problem)
    context.addIssue({
      code: "custom",
      path: [problem.index, "showWhen"],
      message: problem.message,
    })
}
